import type { DocumentData, FirestoreError, QuerySnapshot } from 'firebase/firestore';
import { collection, deleteField, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type { Draft } from '../components/TenderDetails/types';
import { getLoadingKey } from '../components/TenderDetails/utils';
import { compileDraft, deleteDraftForTenderReview, generateVisualExport } from '../services/api';
import { firestore } from '../services/firebase';
import type { User } from '../types/auth';
import type { Tender } from '../types/tender';
import { logDocumentActivity, logTenderActivity } from '../utils/activityLogger';

export interface UseTenderDraftsProps {
  tenderId?: string | undefined;
  tender: Tender | null;
  account: (User & { clientId?: string; actualDefaultBucketName?: string }) | null;
  isAuthLoading: boolean;
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
  setLoadingStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setDocumentCategories: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  handleDownload: (
    gsURI: string,
    fileTypeIdentifier: string,
    defaultFileName: string
  ) => Promise<void>;
}

export const useTenderDrafts = ({
  tenderId,
  tender,
  account,
  isAuthLoading,
  showSnackbar,
  setLoadingStates,
  setDocumentCategories,
  handleDownload,
}: UseTenderDraftsProps) => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [_localLoading, _setLocalLoading] = useState(false);
  const isGeneratingPresentation = tender?.isGeneratingPresentation || false;
  const isGeneratingPdf = tender?.isGeneratingPdf || false;
  const presentationError = tender?.presentationError;
  const pdfError = tender?.pdfError;
  useEffect(() => {
    if (isAuthLoading || !account?.uid || !tenderId || !account?.clientId || !tender) {
      setDrafts([]);
      return undefined;
    }

    const draftsRef = collection(firestore, `tenders/${tenderId}/draftsForReview`);

    const draftsUnsubscribe = onSnapshot(
      draftsRef,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const draftsData = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Draft[];
        setDrafts(draftsData);
      },
      (error: FirestoreError) => {
        if (error.code === 'permission-denied') {
          showSnackbar('Permission denied loading drafts.', 'error');
        }
      }
    );

    return () => {
      draftsUnsubscribe();
    };
  }, [tenderId, account?.uid, account?.clientId, isAuthLoading, showSnackbar, tender]);

  const handleCompileDraft = async () => {
    if (!tender?.id || !account?.clientId) {
      return;
    }

    // Optimistic update
    try {
      const tenderDocRef = doc(firestore, 'tenders', tender.id);
      await updateDoc(tenderDocRef, {
        'compileStatus.status': 'processing',
        'compileStatus.startedAt': new Date().toISOString(),
        compiledFileLink: deleteField(),
      });
    } catch (_e) {
      // ignore
    }

    try {
      const response = await compileDraft(tender.id, account.clientId);
      if (response.success) {
        showSnackbar('Compilation Complete', 'success');
        logTenderActivity.updated({
          id: tender.id,
          title: tender.title,
          updateType: 'compile_draft_started',
        });
      } else {
        throw new Error('Compilation failed');
      }
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg = err.response?.data?.message || err.message || 'Compilation failed';
      showSnackbar(msg, 'error');
      try {
        const tenderDocRef = doc(firestore, 'tenders', tender.id);
        await updateDoc(tenderDocRef, { 'compileStatus.status': 'error' });
      } catch (_) {
        // Ignore error during error status update
      }
    }
  };

  const handleResetCompile = async () => {
    if (!tender?.id) {
      return;
    }
    try {
      const tenderDocRef = doc(firestore, 'tenders', tender.id);
      await updateDoc(tenderDocRef, {
        'compileStatus.status': 'idle',
        compiledFileLink: null,
      });
      logTenderActivity.updated({
        id: tender.id,
        title: tender.title,
        updateType: 'compile_reset',
      });
    } catch (_e) {
      showSnackbar('Failed to reset compilation status', 'error');
    }
  };

  const extractFilenameFromUrl = (url: string): string => {
    try {
      const decodedUrl = decodeURIComponent(url);
      const parts = decodedUrl.split('/');
      const filenameWithParams = parts.pop();
      if (!filenameWithParams) {
        return 'document.docx';
      }
      return filenameWithParams.split('?')[0] || 'document.docx';
    } catch (_e) {
      return 'document.docx';
    }
  };

  const handleDownloadCompile = async () => {
    const link = tender?.compiledFileLink || tender?.draftFileLink;
    if (!link) {
      return;
    }
    const filename = extractFilenameFromUrl(link);
    handleDownload(link, 'final_proposal', filename);
  };

  const handleDownloadPresentation = async () => {
    const link = tender?.presentationLink;
    if (!link) {
      return;
    }
    const filename = extractFilenameFromUrl(link);
    handleDownload(link, 'presentation', filename);
  };

  const handleDownloadPdf = async () => {
    const link = tender?.pdfLink;
    if (!link) {
      return;
    }
    const filename = extractFilenameFromUrl(link);
    handleDownload(link, 'pdf', filename);
  };

  const handleDeleteDraft = async (draft: Draft) => {
    const draftFileName = draft.filename || draft.name;
    if (
      !account?.uid ||
      !tender?.id ||
      !draftFileName ||
      !account?.clientId ||
      !account?.actualDefaultBucketName
    ) {
      showSnackbar('Missing critical information for deleting draft.', 'error');
      return;
    }

    const loadingKey = getLoadingKey(tender.id, 'delete_draft', draftFileName);
    setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));

    try {
      await deleteDraftForTenderReview({
        userId: account.uid,
        clientId: account.clientId,
        tenderId: tender.id,
        generatedFileName: draftFileName,
        actualBucketName: account.actualDefaultBucketName,
      });

      const displayFileName = draft.originalFilename || draftFileName;
      const displayName = displayFileName.split('.').slice(0, -1).join('.') || displayFileName;

      showSnackbar(`Deleted draft: ${displayName}`, 'success', 2000);
      logDocumentActivity.deleted({
        type: 'tender_draft',
        fileName: draftFileName,
        source: `tender_details_dialog/${tender.id}`,
      });
      setDrafts((prev) => prev.filter((d) => (d.filename || d.name) !== draftFileName));
      setDocumentCategories((prev) => {
        const newCategories = { ...prev };
        delete newCategories[draftFileName];
        return newCategories;
      });
    } catch (err: unknown) {
      const error = err as Error;
      const displayName = draftFileName.split('.').slice(0, -1).join('.') || draftFileName;
      showSnackbar(`Failed to delete draft ${displayName}: ${error.message}`, 'error', 3000);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleGeneratePresentation = async (
    exportFormat: 'pptx' | 'pdf' | 'html' = 'pptx',
    numSlides: number = 10,
    engine?: string
  ) => {
    if (!tender?.id || !account?.clientId) {
      return;
    }
    try {
      // Optimistically update Firestore to trigger the UI spinner across reloads
      await updateDoc(doc(firestore, `tenders/${tender.id}`), {
        isGeneratingPresentation: true,
      });
      showSnackbar('Generating presentation...', 'info');

      const jobId = crypto.randomUUID();
      const response = await generateVisualExport(
        tender.id,
        account.clientId,
        'presentation',
        exportFormat,
        numSlides,
        engine,
        jobId
      );
      if (response.success) {
        showSnackbar('Presentation generated successfully', 'success');
      } else {
        showSnackbar(`Error: ${response.error || 'Failed to generate presentation'}`, 'error');
      }
    } catch (error) {
      // The Python backend is designed to keep running even if the proxy times out.
      // We only notify the user if the error is explicitly not a timeout.
      const msg = (error as Error).message;
      if (msg.toLowerCase().includes('timeout') || msg.toLowerCase().includes('network')) {
        showSnackbar(
          'The generation is taking longer than expected. It will continue running in the background.',
          'info'
        );
      } else {
        showSnackbar(`Failed to generate presentation: ${msg}`, 'error');
      }
    }
  };

  const handleGeneratePdf = async () => {
    if (!tender?.id || !account?.clientId) {
      return;
    }
    try {
      // Optimistically update Firestore to trigger the UI spinner across reloads
      await updateDoc(doc(firestore, `tenders/${tender.id}`), {
        isGeneratingPdf: true,
      });
      showSnackbar('Generating PDF...', 'info');

      const jobId = crypto.randomUUID();
      const response = await generateVisualExport(
        tender.id,
        account.clientId,
        'pdf',
        undefined,
        undefined,
        undefined,
        jobId
      );
      if (response.success) {
        showSnackbar('PDF generated successfully', 'success');
      } else {
        showSnackbar(`Error: ${response.error || 'Failed to generate PDF'}`, 'error');
      }
    } catch (error) {
      // The Python backend is designed to keep running even if the proxy times out.
      // We only notify the user if the error is explicitly not a timeout.
      const msg = (error as Error).message;
      if (msg.toLowerCase().includes('timeout') || msg.toLowerCase().includes('network')) {
        showSnackbar(
          'The generation is taking longer than expected. It will continue running in the background.',
          'info'
        );
      } else {
        showSnackbar(`Failed to generate PDF: ${msg}`, 'error');
      }
    }
  };

  const handleResetPresentation = async () => {
    if (!tender?.id) {
      return;
    }
    try {
      await updateDoc(doc(firestore, `tenders/${tender.id}`), {
        isGeneratingPresentation: false,
        presentationError: deleteField(),
      });
      showSnackbar('Presentation status reset.', 'success');
    } catch (_error) {
      showSnackbar('Failed to reset presentation status.', 'error');
    }
  };

  const handleResetPdf = async () => {
    if (!tender?.id) {
      return;
    }
    try {
      await updateDoc(doc(firestore, `tenders/${tender.id}`), {
        isGeneratingPdf: false,
        pdfError: deleteField(),
      });
      showSnackbar('PDF status reset.', 'success');
    } catch (_error) {
      showSnackbar('Failed to reset PDF status.', 'error');
    }
  };

  return {
    drafts,
    handleCompileDraft,
    handleResetCompile,
    handleDownloadCompile,
    handleDeleteDraft,
    localLoading: _localLoading,
    handleGeneratePresentation,
    handleGeneratePdf,
    handleDownloadPresentation,
    handleDownloadPdf,
    handleResetPresentation,
    handleResetPdf,
    isGeneratingPresentation,
    isGeneratingPdf,
    presentationError,
    pdfError,
  };
};
